import axios from "../../Axios";
import { Urls } from "../../Urls";
import { currentRequestTimeStamp, Activity } from "../../Constants";
import { MastersChangeLog } from "../../Components/CompanyProfileNew/Utility";
import { buildEnquiryApiPayload } from "../../modules/spot/spotLaneFormatter";
import { useMutation, useQuery } from "@tanstack/react-query";

export const postSpotEnquiry = async (companyMail, lanes) => {
  const enquiries = lanes.map((lane) => buildEnquiryApiPayload(lane, "lorri_spot"));
  const res = await axios.post(`${Urls.spot}?field=create_enquiry_v2`, { enquiries });
  return res.data;
};

export const usePostSpotEnquiryMutation = () => {
  return useMutation({
    mutationFn: ({ companyMail, lanes }) => postSpotEnquiry(companyMail, lanes)
  });
};

/**
 * Posts a planning spot enquiry creation request.
 * Now aligned with create_enquiry_v2 and individual enquiry submission.
 *
 * @param {Array} groupedEnquiries - Grouped enquiry objects (by email) - 
 *                                  each containing an enquiry_lane_details array.
 * @returns {Promise<Object>} API response data.
 */
export const postPlanningEnquiry = async (groupedEnquiries) => {
  const allLanes = groupedEnquiries.flatMap((g) => g.enquiry_lane_details);
  const enquiries = allLanes.map((lane) => buildEnquiryApiPayload(lane, "planning"));
  const res = await axios.post(`${Urls.spot}?field=create_enquiry_v2`, { enquiries });
  return res.data;
};

export const usePostPlanningEnquiryMutation = () => {
  return useMutation({
    mutationFn: postPlanningEnquiry
  });
};

/**
 * Marks planning load IDs as having had a spot enquiry created.
 * Used after successful enquiry submission in planning flow.
 *
 * @param {Array<string>} loadIds - Load IDs to update.
 * @returns {Promise<Object>} API response data.
 */
export const updatePlanningLoads = async (loadIds) => {
  const res = await axios.put(
    `${Urls.edit_planning_loads}?field=plan_update&action=create_spot`,
    { load_ids: loadIds },
  );
  return res.data;
};

export const useUpdatePlanningLoadsMutation = () => {
  return useMutation({
    mutationFn: updatePlanningLoads
  });
};

/**
 * Fetches company-level enquiry configuration (expiry settings, etc.).
 *
 * @param {string} companyId - The company ID to fetch settings for.
 * @returns {Promise<Object>} API response data (array; first element has enquiry config).
 */
export const fetchCompanyEnquirySettings = async (companyId) => {
  const res = await axios.get(
    `${Urls.companyProfile}?company_id=${companyId}&projections_enable=enquiry`,
  );
  return res.data;
};

export const useCompanyEnquirySettingsQuery = (companyId, options) => {
  return useQuery({
    queryKey: ['companyEnquirySettings', companyId],
    queryFn: () => fetchCompanyEnquirySettings(companyId),
    ...options
  });
};

export const rejectOpenEnquiries = async (enquiries) => {
  const res = await axios.put(`${Urls.spot}?field=bulk_operation_v2&operation=rejection`, enquiries);
  return res.data;
};

export const useRejectOpenEnquiriesMutation = () => {
  return useMutation({
    mutationFn: rejectOpenEnquiries
  });
};

/**
 * Groups a flat array of lane objects into the enquiry payload format
 * expected by the planning API (grouped by assigned user email).
 *
 * Consolidates the repeated grouping pattern from NewEnquiry.createEnquiry().
 *
 * @param {Array}  lanes - Flat list of lane objects with .email fields.
 * @param {string} company - Company short name derived from email domain.
 * @returns {Array} Array of grouped enquiry payload objects.
 */
export const groupEnquiriesByEmail = (lanes, company) => {
  const grouped = [];
  lanes.forEach((enquiry) => {
    const existing = grouped.find((a) => a.email === enquiry.email);
    if (existing) {
      existing.enquiry_lane_details.push(enquiry);
    } else {
      grouped.push({
        timestamp_utc: currentRequestTimeStamp,
        user_id: enquiry.user_id,
        user_name: enquiry.user_name,
        email: enquiry.email,
        company,
        domain: enquiry.email ? enquiry.email.split("@")[1] : "",
        enquiry_lane_details: [enquiry],
      });
    }
  });
  return grouped;
};

/**
 * Validates that the enquiry expiry time is configured in company settings before submission.
 * Throws a user-friendly error if expiry is required but not set.
 *
 * @param {string} companyId - The company ID.
 * @throws {Error} If expiry is not configured when auto-expiry is enabled.
 */
export const validateEnquiryExpiry = async (companyId) => {
  if (!companyId) return; // No company ID — skip validation
  try {
    const resData = await fetchCompanyEnquirySettings(companyId);
    const enquiryCfg = resData?.[0]?.enquiry;
    if (enquiryCfg?.enquiry_auto_expiry_enable !== "no" && !enquiryCfg?.enquiry_expiry) {
      throw new Error("Please update expiry time from settings");
    }
  } catch (err) {
    if (err.message === "Please update expiry time from settings") throw err;
    console.error(err);
    throw new Error("Please update expiry time from settings");
  }
};

/**
 * Handles the full spot enquiry submission process (replaces createEnquiry, enquiryApiCall, enquiryPlanningApiCall).
 */
export const submitSpotEnquiries = async (
  validatedData,
  {
    companyProfile,
    planningbutton,
    company_mail,
    company_id,
    userid,
    rspl,
    update_orders,
    TrackMixPanel
  }, email, userName
) => {

  // 1. Validate Expiry Settings
  const companyId = companyProfile?.domain_data?.company_id;
  await validateEnquiryExpiry(companyId);
  const finalisedEnquiries = validatedData;
  Activity([
    {
      screen: "LoRRI Spot",
      module: "My Enquiries",
      user_activity: "Submit Button Clicked",
      timestamp: currentRequestTimeStamp(),
      userName: userName,
      userEmail: email,
      companyname: email
        ? email.split("@")[1].split(".")[0]
        : ""
    },
  ]);

  if (planningbutton) {

    const company = company_mail.split("@")[1].split(".")[0];
    const grouped = groupEnquiriesByEmail(finalisedEnquiries, company);

    if (TrackMixPanel) {
      TrackMixPanel({
        ModuleName: "my_enquiries",
        ScreenName: "LoRRI-Trucksavailable",
        Definition: "User has clicked on submit button",
        Data: grouped.map((activity) => activity.enquiry_lane_details.map((lane) =>
          `${lane.lane_details?.origin?.location_name} to ${lane.lane_details?.destination?.location_name}, ${Array.isArray(lane.lane_details?.truck_types) ? lane.lane_details.truck_types.map((t) => t?.label).join(", ") : lane.lane_details?.truck_types?.label || ""} `
        ).join(",")).join(","),
      });
    }

    await postPlanningEnquiry(grouped);

    // Planning specific success logic
    try {
      finalisedEnquiries.forEach((enquiry) => {
        const originName = enquiry.lane_details?.origin?.location?.location || enquiry.lane_details?.origin?.location_name || "";
        const destName = enquiry.lane_details?.destination?.location?.location || enquiry.lane_details?.destination?.location_name || "";

        const change_log_payload = {
          action: "Create Enquiry",
          screen: "planning",
          user_email: userDetails?.email,
          user_id: userid,
          date: enquiry.lane_details?.due_date || (rspl && rspl[0]?.item?.dispatch_date),
          timestamp: currentRequestTimeStamp(),
          company_id: company_id,
          change_activity: {
            lane_name: `${originName} to ${destName}`,
            load_id: enquiry.load_id,
          },
        };
        MastersChangeLog(null, null, null, null, company_id, null, null, null, change_log_payload);
      });
    } catch (error) {
      console.log(error);
    }

    // Update loads
    const load_ids = finalisedEnquiries.flatMap((lane) => lane.load_id || (lane.enquiry_lane_details ? lane.enquiry_lane_details.map(d => d.load_id) : []));
    if (load_ids.length > 0) {
      try {
        const data = await updatePlanningLoads(load_ids);
        if (update_orders) data.loads.forEach((load) => update_orders(load));
      } catch (error) {
        console.log(error);
      }
    }

    return { finalisedEnquiries };
  } else {

    if (TrackMixPanel) {
      TrackMixPanel({
        ModuleName: "my_enquiries",
        ScreenName: "LoRRI-Trucksavailable",
        Definition: "User has clicked on submit button",
        Data: finalisedEnquiries.map((lane) => `${lane?.lane_details?.origin?.location_name} to ${lane?.lane_details?.destination?.location_name}, ${Array.isArray(lane?.lane_details?.truck_types) ? lane?.lane_details?.truck_types.map((t) => t?.label).join(", ") : lane?.lane_details?.truck_types?.label || ""} `).join(","),
      });
    }

    await postSpotEnquiry(company_mail, finalisedEnquiries);
    return { finalisedEnquiries };
  }
};
